import {
  createFileRoute,
  useNavigate,
  useSearch,
  Await,
} from "@tanstack/react-router";
import {
  Stack,
  CircularProgress,
  IconButton,
  Box,
  Grid2 as Grid,
  Paper,
  Typography,
  Button,
  Snackbar,
  Alert,
} from "@mui/material";
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
} from "@mui/icons-material";
import { z } from "zod";
import dayjs from "dayjs";
import { useState, useEffect } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";

import { useTranslation } from "react-i18next";

import { ensureAuthenticated } from "../utils/auth.ts";
import { GenericTable, TableColumn } from "../components/GenericTable";
import { TemperatureDialog } from "../components/CoolingRegime/TemperatureDialog";
import { PriorityDialog } from "../components/CoolingRegime/PriorityDialog";

import { Route as CoolingRegimesRoute } from "../routes/cooling-regimes";
import { routerHead } from "../utils/head.ts";
import { createCoolingRegimeTemperatureDeferredLoader } from "../loaders/coolingRegimeTemperatureLoader";
import type { CoolingRegimeTemperatureLoaderResult } from "../loaders/coolingRegimeTemperatureLoader";
import type { TemperatureRow } from "../mappers/coolingRegimeTemperatureMapper";
import { createCoolingRegimePriorityDeferredLoader } from "../loaders/coolingRegimePriorityLoader";
import type { CoolingRegimePriorityLoaderResult } from "../loaders/coolingRegimePriorityLoader";
import type { PriorityRow } from "../mappers/coolingRegimePriorityMapper";
import {
  createCoolingRegimeTemperature,
  updateCoolingRegimeTemperature,
  deleteCoolingRegimeTemperature,
} from "../services/coolingRegimeTemperatureService";
import {
  createCoolingRegimePriority,
  updateCoolingRegimePriority,
  deleteCoolingRegimePriority,
} from "../services/coolingRegimePriorityService";
import { extractErrorMessage } from "../utils/apiError";
import { useFeatureFlag, Feature } from "../hooks/useFeatureFlag";

export interface CoolingRegimeRow {
  koelregimeCode: number;
  omschrijving: string;
  aangemaaktDoor: string;
  aangemaaktOp: string;
  laatstBewerktDoor: string;
  laatstBewerktOp: string;
}

const temperatureColumns = (
  t: (key: string) => string,
  onEdit: (row: TemperatureRow) => void,
  onDelete: (row: TemperatureRow) => void,
  canMutate: boolean = false,
): TableColumn<TemperatureRow>[] => [
  {
    key: "coolingRegimeTemperatureId",
    label: t("temperature.temperatureId"),
  },
  { key: "description", label: t("temperature.description") },
  { key: "lastModified", label: t("temperature.lastModified") },
  { key: "lastModifiedBy", label: t("temperature.lastModifiedBy") },
  {
    key: "actions",
    label: "Acties",
    render: (_value, row) =>
      canMutate ? (
        <Box sx={{ display: "flex", gap: 1 }}>
          <IconButton size="small" onClick={() => onEdit(row)} color="primary">
            <EditIcon fontSize="small" />
          </IconButton>
          <IconButton size="small" onClick={() => onDelete(row)} color="error">
            <DeleteIcon fontSize="small" />
          </IconButton>
        </Box>
      ) : null,
  },
];

const priorityColumns = (
  t: (key: string) => string,
  onEdit: (row: PriorityRow) => void,
  onDelete: (row: PriorityRow) => void,
  canMutate: boolean = false,
): TableColumn<PriorityRow>[] => [
  {
    key: "coolingRegimePriorityId",
    label: t("priority.priorityId"),
  },
  { key: "description", label: t("priority.description") },
  { key: "lastModified", label: t("priority.lastModified") },
  { key: "lastModifiedBy", label: t("priority.lastModifiedBy") },
  {
    key: "actions",
    label: "Acties",
    render: (_value, row) =>
      canMutate ? (
        <Box sx={{ display: "flex", gap: 1 }}>
          <IconButton size="small" onClick={() => onEdit(row)} color="primary">
            <EditIcon fontSize="small" />
          </IconButton>
          <IconButton size="small" onClick={() => onDelete(row)} color="error">
            <DeleteIcon fontSize="small" />
          </IconButton>
        </Box>
      ) : null,
  },
];

const searchSchema = z.object({
  page: z.string().optional(),
  size: z.string().optional(),
  temperatureId: z.string().optional(),
  temperatureDescription: z.string().optional(),
  temperatureModifiedDate: z.string().optional(),
  temperatureModifiedBy: z.string().optional(),
  priorityId: z.string().optional(),
  priorityDescription: z.string().optional(),
  priorityModifiedDate: z.string().optional(),
  priorityModifiedBy: z.string().optional(),
});

type SearchParams = z.infer<typeof searchSchema>;

export const Route = createFileRoute("/cooling-regimes")({
  beforeLoad: ensureAuthenticated,
  loaderDeps: ({ search }: { search: SearchParams }) => ({
    page: search.page,
    size: search.size,
    temperatureId: search.temperatureId,
    temperatureDescription: search.temperatureDescription,
    temperatureModifiedDate: search.temperatureModifiedDate,
    temperatureModifiedBy: search.temperatureModifiedBy,
    priorityId: search.priorityId,
    priorityDescription: search.priorityDescription,
    priorityModifiedDate: search.priorityModifiedDate,
    priorityModifiedBy: search.priorityModifiedBy,
  }),
  loader: async ({ location }: { location: { search: SearchParams } }) => {
    const temperatureFilters = {
      page: location.search.page ? parseInt(location.search.page) : 1,
      pageSize: location.search.size ? parseInt(location.search.size) : 10,
      temperatureId: location.search.temperatureId,
      description: location.search.temperatureDescription,
      modifiedDate: location.search.temperatureModifiedDate,
      modifiedBy: location.search.temperatureModifiedBy,
    };

    const priorityFilters = {
      priorityId: location.search.priorityId,
      description: location.search.priorityDescription,
      modifiedDate: location.search.priorityModifiedDate,
      modifiedBy: location.search.priorityModifiedBy,
    };

    return {
      temperatures:
        createCoolingRegimeTemperatureDeferredLoader(temperatureFilters),
      priorities: createCoolingRegimePriorityDeferredLoader(priorityFilters),
    };
  },
  validateSearch: searchSchema,
  component: RouteComponent,
  head: routerHead("Koelregimes"),
});

function RouteComponent() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const search = useSearch({ from: CoolingRegimesRoute.id });
  const loaderData = Route.useLoaderData();
  const queryClient = useQueryClient();
  const canMutateTemperature = useFeatureFlag(
    Feature.MutateCoolingRegimeTemperature,
  );
  const canMutatePriority = useFeatureFlag(
    Feature.MutateCoolingRegimePriority,
  );

  // Snackbar state for error toasts
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState<string>("");

  // Temperature dialog state
  const [temperatureDialogOpen, setTemperatureDialogOpen] = useState(false);
  const [editingTemperature, setEditingTemperature] =
    useState<TemperatureRow | null>(null);

  // Priority dialog state
  const [priorityDialogOpen, setPriorityDialogOpen] = useState(false);
  const [editingPriority, setEditingPriority] = useState<PriorityRow | null>(
    null,
  );

  // Temperature mutations
  const createTemperatureMutation = useMutation({
    mutationFn: (description: string) =>
      createCoolingRegimeTemperature(description),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["coolingRegimeTemperatures"],
      });
      navigate({ to: CoolingRegimesRoute.fullPath, search });
    },
    onError: (error: unknown) => {
      setSnackbarMessage(extractErrorMessage(error));
      setSnackbarOpen(true);
    },
  });

  const updateTemperatureMutation = useMutation({
    mutationFn: ({
      id,
      description,
      createdBy,
      created,
    }: {
      id: number;
      description: string;
      createdBy: string;
      created: Date | string | null;
    }) => updateCoolingRegimeTemperature(id, description, createdBy, created),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["coolingRegimeTemperatures"],
      });
      navigate({ to: CoolingRegimesRoute.fullPath, search });
    },
    onError: (error: unknown) => {
      setSnackbarMessage(extractErrorMessage(error));
      setSnackbarOpen(true);
    },
  });

  const deleteTemperatureMutation = useMutation({
    mutationFn: deleteCoolingRegimeTemperature,
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["coolingRegimeTemperatures"],
      });
      navigate({ to: CoolingRegimesRoute.fullPath, search });
    },
    onError: (error: unknown) => {
      setSnackbarMessage(extractErrorMessage(error));
      setSnackbarOpen(true);
    },
  });

  // Priority mutations
  const createPriorityMutation = useMutation({
    mutationFn: (description: string) =>
      createCoolingRegimePriority(description),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["coolingRegimePriorities"] });
      navigate({ to: CoolingRegimesRoute.fullPath, search });
    },
    onError: (error: unknown) => {
      setSnackbarMessage(extractErrorMessage(error));
      setSnackbarOpen(true);
    },
  });

  const updatePriorityMutation = useMutation({
    mutationFn: ({
      id,
      description,
      createdBy,
      created,
    }: {
      id: number;
      description: string;
      createdBy: string;
      created: Date | string | null;
    }) => updateCoolingRegimePriority(id, description, createdBy, created),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["coolingRegimePriorities"] });
      navigate({ to: CoolingRegimesRoute.fullPath, search });
    },
    onError: (error: unknown) => {
      setSnackbarMessage(extractErrorMessage(error));
      setSnackbarOpen(true);
    },
  });

  const deletePriorityMutation = useMutation({
    mutationFn: deleteCoolingRegimePriority,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["coolingRegimePriorities"] });
      navigate({ to: CoolingRegimesRoute.fullPath, search });
    },
    onError: (error: unknown) => {
      setSnackbarMessage(extractErrorMessage(error));
      setSnackbarOpen(true);
    },
  });

  // Temperature handlers
  const handleCreateTemperature = () => {
    setEditingTemperature(null);
    setTemperatureDialogOpen(true);
  };

  const handleEditTemperature = (row: TemperatureRow) => {
    setEditingTemperature(row);
    setTemperatureDialogOpen(true);
  };

  const handleDeleteTemperature = async (row: TemperatureRow) => {
    if (
      window.confirm(
        `Weet je zeker dat je "${row.description}" wilt verwijderen?`,
      )
    ) {
      await deleteTemperatureMutation.mutateAsync(
        row.coolingRegimeTemperatureId,
      );
    }
  };

  const handleTemperatureSubmit = async (description: string) => {
    if (editingTemperature) {
      await updateTemperatureMutation.mutateAsync({
        id: editingTemperature.coolingRegimeTemperatureId,
        description,
        createdBy: editingTemperature.createdBy,
        created: editingTemperature.created,
      });
    } else {
      await createTemperatureMutation.mutateAsync(description);
    }
  };

  // Priority handlers
  const handleCreatePriority = () => {
    setEditingPriority(null);
    setPriorityDialogOpen(true);
  };

  const handleEditPriority = (row: PriorityRow) => {
    setEditingPriority(row);
    setPriorityDialogOpen(true);
  };

  const handleDeletePriority = async (row: PriorityRow) => {
    if (
      window.confirm(
        `Weet je zeker dat je "${row.description}" wilt verwijderen?`,
      )
    ) {
      await deletePriorityMutation.mutateAsync(row.coolingRegimePriorityId);
    }
  };

  const handlePrioritySubmit = async (description: string) => {
    if (editingPriority) {
      await updatePriorityMutation.mutateAsync({
        id: editingPriority.coolingRegimePriorityId,
        description,
        createdBy: editingPriority.createdBy,
        created: editingPriority.created,
      });
    } else {
      await createPriorityMutation.mutateAsync(description);
    }
  };

  return (
    <>
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={4000}
        onClose={() => setSnackbarOpen(false)}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert severity="error" onClose={() => setSnackbarOpen(false)} sx={{ width: "100%" }}>
          {snackbarMessage}
        </Alert>
      </Snackbar>

      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: 3,
        }}
      >
        <Typography variant="h1">Koelregime opties overzicht</Typography>
      </Box>

        <Grid container spacing={3}>
          <Grid size={{ xs: 12, lg: 6 }}>
            <Paper
              elevation={1}
              sx={{
                p: 2,
                position: "relative",
                minHeight: 200,
                "& > .MuiCircularProgress-root": {
                  position: "absolute",
                  top: "50%",
                  left: "50%",
                  transform: "translate(-50%, -50%)",
                },
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  mb: 2,
                }}
              >
                <Typography variant="h2">
                  {t("temperature.overviewTitle")}
                </Typography>
                {canMutateTemperature && (
                  <IconButton
                    color="primary"
                    onClick={handleCreateTemperature}
                    sx={{
                      bgcolor: "primary.main",
                      color: "white",
                      "&:hover": { bgcolor: "primary.dark" },
                    }}
                  >
                    <AddIcon />
                  </IconButton>
                )}
              </Box>
              <Await
                promise={loaderData.temperatures.data}
                fallback={<CircularProgress />}
              >
                {(data: CoolingRegimeTemperatureLoaderResult) => {
                  if (data.error) {
                    return <div>Error: {data.error}</div>;
                  }
                  return (
                    <GenericTable
                      columns={temperatureColumns(
                        t,
                        handleEditTemperature,
                        handleDeleteTemperature,
                        canMutateTemperature,
                      )}
                      rows={data.rows}
                      rowCount={data.totalCount}
                      defaultPageSize={10}
                      pageSizeOptions={[10, 25, 50, 100]}
                      onPageChange={(page) => {
                        navigate({
                          to: CoolingRegimesRoute.fullPath,
                          search: { ...search, page: String(page) },
                        });
                      }}
                      onPageSizeChange={(pageSize) => {
                        navigate({
                          to: CoolingRegimesRoute.fullPath,
                          search: {
                            ...search,
                            size: String(pageSize),
                            page: "1",
                          },
                        });
                      }}
                    />
                  );
                }}
              </Await>
            </Paper>
          </Grid>

          <Grid size={{ xs: 12, lg: 6 }}>
            <Paper
              elevation={1}
              sx={{
                p: 2,
                position: "relative",
                minHeight: 200,
                "& > .MuiCircularProgress-root": {
                  position: "absolute",
                  top: "50%",
                  left: "50%",
                  transform: "translate(-50%, -50%)",
                },
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  mb: 2,
                }}
              >
                <Typography variant="h2">
                  {t("priority.overviewTitle")}
                </Typography>
                {canMutatePriority && (
                  <IconButton
                    color="primary"
                    onClick={handleCreatePriority}
                    sx={{
                      bgcolor: "primary.main",
                      color: "white",
                      "&:hover": { bgcolor: "primary.dark" },
                    }}
                  >
                    <AddIcon />
                  </IconButton>
                )}
              </Box>
              <Await
                promise={loaderData.priorities.data}
                fallback={<CircularProgress />}
              >
                {(data: CoolingRegimePriorityLoaderResult) => {
                  if (data.error) {
                    return <div>Error: {data.error}</div>;
                  }
                  return (
                    <GenericTable
                      columns={priorityColumns(
                        t,
                        handleEditPriority,
                        handleDeletePriority,
                        canMutatePriority,
                      )}
                      rows={data.rows}
                      rowCount={data.totalCount}
                      defaultPageSize={10}
                      pageSizeOptions={[10, 25, 50, 100]}
                      onPageChange={(page) => {
                        navigate({
                          to: CoolingRegimesRoute.fullPath,
                          search: { ...search, page: String(page) },
                        });
                      }}
                      onPageSizeChange={(pageSize) => {
                        navigate({
                          to: CoolingRegimesRoute.fullPath,
                          search: {
                            ...search,
                            size: String(pageSize),
                            page: "1",
                          },
                        });
                      }}
                    />
                  );
                }}
              </Await>
            </Paper>
          </Grid>
        </Grid>

      <TemperatureDialog
        open={temperatureDialogOpen}
        onClose={() => setTemperatureDialogOpen(false)}
        onSubmit={handleTemperatureSubmit}
        initialDescription={editingTemperature?.description || ""}
        title={
          editingTemperature
            ? "Koeltemperatuur bewerken"
            : "Koeltemperatuur aanmaken"
        }
      />

      <PriorityDialog
        open={priorityDialogOpen}
        onClose={() => setPriorityDialogOpen(false)}
        onSubmit={handlePrioritySubmit}
        initialDescription={editingPriority?.description || ""}
        title={
          editingPriority
            ? "Koelprioriteit bewerken"
            : "Koelprioriteit aanmaken"
        }
      />
    </>
  );
}
